import { makeAutoObservable } from "mobx";
import dayjs, { Dayjs } from "dayjs";
import { t } from "i18next";
import { Planning2TableStore } from "../Planning2Table/Planning2TableStore";
import {
  Planning2Column,
  Planning2ColumnKey,
} from "../Planning2Table/Planning2Column";
import { ZPlanning2Row, ZPlanning2Task } from "../../ZPlanning2Row";
import { buildLeftColumns } from "./buildLeftColumns";
import { Planning2DatesStore } from "../Planning2Dates/Planning2DatesStore";
import { Bar } from "./Pl2GanttView/GanttViewTypes";

export type GanttLeftRow = ZPlanning2Row & {
  level?: number;
};

export type GanttDay = {
  key: string;
  date: Dayjs;
  bk?: "dayOff" | "today";
  dow: number;
};

export type GanttMonth = {
  key: string;
  year: number;
  index: number;
  name: string;
  days: number;
  x: number;
  width: number;
};

export type GanttWeek = {
  key: string;
  begin: Dayjs;
  end: Dayjs;
  x: number;
  width: number;
};

export type GanttYear = {
  key: string;
  year: number;
  x: number;
  width: number;
};

export type GanttTask = {
  key: string;
  plan?: Bar;
  actual?: Bar;
  forecast?: Bar;
  delay?: Bar;
  work?: Bar;
};

export const getTaskBars = (task: GanttTask): Bar[] =>
  [task.plan, task.actual, task.forecast, task.delay, task.work].filter(
    (b) => !!b,
  );

export type GanttArrow = {
  key: string;
  points: [number, number][];
};

export type ScaleMode = "day" | "month" | "week";

export const scaleModes: Record<ScaleMode, string> = {
  day: "Дни",
  week: "Недели",
  month: "Месяцы",
};

export class Planning2GanttStore {
  static cellHeight = 38; // <-- это значение должно совпадать с соответствующим в less-файле

  static headHeight = 48; // <-- это значение должно совпадать с соответствующим в less-файле

  constructor(
    public readonly tableStore: Planning2TableStore,
    public readonly datesStore: Planning2DatesStore,
  ) {
    const { settings } = tableStore;
    this.setShowGantt(settings.showGantt ?? false);
    this.setWorldScale(settings.timeScale ?? this.worldScale);
    this.setRightWidth(settings.rightWidth ?? this.rightWidth);
    this.setArrowsVisible(settings.showArrows ?? this.arrowsVisible);
    makeAutoObservable(this);
  }

  showGantt = false; // <-- Пока договорились не показывать диаграмму по дефолту

  setShowGantt(value: boolean) {
    this.showGantt = value;
  }

  changeShowGantt(value: boolean) {
    this.setShowGantt(value);
    this.tableStore.updateSettings({ showGantt: value });
  }

  // Это желаемая ширина. Реальная ширина учитывает min-width, max-width from .leftPart
  rightWidth: number | string = "50%";

  setRightWidth(width: number | string) {
    this.rightWidth = width;
  }

  changeRightWidth(width: number | string) {
    this.setRightWidth(width);
    this.tableStore.updateSettings({ rightWidth: width });
  }

  // left part

  get leftColumns(): Planning2Column[] {
    return buildLeftColumns(this.tableStore.finalColumns, this);
  }

  get leftTemplateColumns(): string {
    return getLeftTemplateColumns(this.leftColumns);
  }

  get leftFixedTemplateColumns(): string {
    return getLeftTemplateColumns(this.leftColumns.slice(0, 2));
  }

  get leftRows(): GanttLeftRow[] {
    const result: GanttLeftRow[] = [];
    const { tableStore } = this;
    tableStore.visibleRows.forEach((primaryRow) => {
      result.push({ ...primaryRow, level: 0 });
      if (
        primaryRow.kind === "group" &&
        tableStore.expanded.has(primaryRow.id)
      ) {
        primaryRow.children.forEach((secondaryRow) => {
          result.push({ ...secondaryRow, level: 1 });
        });
      }
    });
    return result;
  }

  worldScale = 60; // Абстрактный масштаб. От 0 до 100.

  setWorldScale(scale: number) {
    this.worldScale = scale;
  }

  changeWorldScale(scale: number) {
    this.setWorldScale(scale);
    this.tableStore.updateSettings({ timeScale: scale });
  }

  arrowsVisible = true;

  setArrowsVisible(flag: boolean) {
    this.arrowsVisible = flag;
  }

  changeArrowsVisible(flag: boolean) {
    this.setArrowsVisible(flag);
    this.tableStore.updateSettings({ showArrows: flag });
  }

  get arrowsDisabled(): boolean {
    //  Если скрыт хотя бы один из слоёв Прогноз или Факт, то все стрелки связей на Диаграмме Ганта скрыты
    return !(this.isForecastVisible && this.isActualVisible);
  }

  get finalArrowVisible() {
    return this.arrowsVisible && !this.arrowsDisabled;
  }

  // все задачи. в том числе скрытые
  get allTasks(): ZPlanning2Task[] {
    const result: ZPlanning2Task[] = [];
    this.tableStore.rows.forEach((row) => {
      if (row.kind === "task") {
        result.push(row);
      } else {
        row.children?.forEach((subRow) => {
          if (subRow.kind === "task") {
            result.push(subRow);
          }
        });
      }
    });
    return result;
  }

  // Первая дата по соглашению вычисляется как начальная минус 10 дней
  get firstDate(): Dayjs {
    const { initialBegin } = this.datesStore;
    const strDate: string | undefined = this.allTasks.reduce(
      (prev: string | undefined, task) => minDate(prev, minTaskDate(task)),
      initialBegin,
    );
    const begin = strDate ? dayjs(strDate) : dayjs();
    return begin.add(-10, "days");
  }

  get lastDate(): Dayjs {
    const { initialEnd, initialBegin } = this.datesStore;
    const strDate: string | undefined = this.allTasks.reduce(
      (prev: string | undefined, task) => maxDate(prev, maxTaskDate(task)),
      maxDate(initialBegin, initialEnd),
    );
    const end = strDate ? dayjs(strDate) : dayjs();
    return end.add(10, "days");
  }

  get totalDays(): number {
    return this.lastDate.diff(this.firstDate, "days");
  }

  static readonly minDayWidth = 1;

  static readonly maxDayWidth = 60;

  get dayWidth(): number {
    // Ширина дня включает левый бордер, но не включает правый
    const minWidth = Planning2GanttStore.minDayWidth;
    const maxWidth = Planning2GanttStore.maxDayWidth;
    //   100 -- (maxWidth-minWidth)
    // scale -- (width - minWidth)
    return Math.round(
      (this.worldScale * (maxWidth - minWidth)) / 100 + minWidth,
    );
  }

  // eslint-disable-next-line class-methods-use-this
  dayWidthToWorldScale(width: number): number {
    const minWidth = Planning2GanttStore.minDayWidth;
    const maxWidth = Planning2GanttStore.maxDayWidth;
    return (100 * (width - minWidth)) / (maxWidth - minWidth);
  }

  static readonly modesMap: {
    width: number; // pixels
    mode: ScaleMode;
  }[] = [
    // обязателен порядок от меньших значений ширины к большим
    { width: 8, mode: "month" },
    { width: 20, mode: "week" },
  ];

  get scaleMode(): ScaleMode {
    const { dayWidth } = this;
    return (
      Planning2GanttStore.modesMap.find(({ width }) => dayWidth <= width)
        ?.mode ?? "day"
    );
  }

  setScaleMode(needMode: ScaleMode) {
    const { modesMap } = Planning2GanttStore;
    const i = modesMap.findIndex(({ mode }) => mode === needMode);
    const prev = modesMap[i - 1];
    const current = modesMap[i];
    let width = 32;
    if (current) {
      width = ((prev?.width ?? 0) + current.width) / 2;
    }
    this.setWorldScale(this.dayWidthToWorldScale(width));
  }

  get totalWidth(): number {
    return this.totalDays * this.dayWidth + 1; // один пиксель на последнюю вертикальную линию
  }

  get totalHeight(): number {
    // Высота ячейки включает нижний бордер, но не верхний.
    return this.leftRows.length * Planning2GanttStore.cellHeight; // самый верхний бордер в заголовке. поэтому в теле его считать не надо
  }

  get years(): GanttYear[] {
    const { dayWidth, days } = this;
    const result: GanttYear[] = [];
    const updatePrev = (index: number) => {
      const prev = result[result.length - 1];
      if (prev) {
        prev.width = index * dayWidth - prev.x;
      }
    };
    let prevYear: number | undefined;
    days.forEach((day, i) => {
      const year = day.date.year();
      if (i === 0 || year !== prevYear) {
        updatePrev(i);
        result.push({
          key: String(year),
          year,
          x: i * dayWidth,
          width: dayWidth,
        });
      }
      prevYear = year;
    });
    updatePrev(days.length);
    return result;
  }

  get weeks(): GanttWeek[] {
    const { days, dayWidth } = this;
    const result: GanttWeek[] = [];
    const update = (weekEnd: GanttDay | undefined) => {
      const lastWeek = result[result.length - 1];
      if (weekEnd && lastWeek) {
        lastWeek.end = weekEnd.date;
        lastWeek.width =
          (lastWeek.end.diff(lastWeek.begin, "day") + 1) * dayWidth;
      }
    };
    days.forEach((day, i) => {
      if (day.dow === 1 || i === 0) {
        update(days[i - 1]);
        const x = i * dayWidth;
        result.push({
          key: day.date.format(),
          x,
          width: dayWidth,
          begin: day.date,
          end: day.date,
        });
      }
    });
    update(days[days.length - 1]);
    return result;
  }

  get days(): GanttDay[] {
    const { totalDays } = this;
    let date = this.firstDate;
    const days: GanttDay[] = [];
    const today = dayjs();
    for (let i = 0; i < totalDays; i++) {
      const dow = date.day();
      const key = date.format();
      let bk: GanttDay["bk"];
      if (today.isSame(date, "day")) {
        bk = "today";
      } else if (dow === 0 || dow === 6) bk = "dayOff";
      const day: GanttDay = { date, bk, key, dow };
      days.push(day);
      date = date.add(1, "day");
    }
    return days;
  }

  static readonly monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Примерная ширина надписи с трёхбуквенныс месяцем: 72 пикс (если не менять CSS)
  static monthName(date: Dayjs): string {
    const id = Planning2GanttStore.monthNames[date.month()];
    return (id && t(`Month3.${id}`)) ?? "";
  }

  get months(): GanttMonth[] {
    const { dayWidth } = this;
    const result: GanttMonth[] = [];
    let cur: GanttMonth | undefined;
    this.days.forEach((d, i) => {
      const x = i * dayWidth;
      if (!cur || cur.index !== d.date.month()) {
        cur = {
          key: `${d.date.year()}:${d.date.month()}`,
          year: d.date.year(),
          index: d.date.month(),
          name: Planning2GanttStore.monthName(d.date),
          days: 1,
          x,
          width: dayWidth,
        } satisfies GanttMonth;
        result.push(cur);
      } else {
        cur.days++;
        cur.width = cur.days * dayWidth;
      }
    });
    return result;
  }

  getX(date: string): number {
    const d = dayjs(date);
    return d.diff(this.firstDate, "days") * this.dayWidth;
  }

  // некоторые фичи проверяют видимость колонок
  isColumnVisible(columnKey: Planning2ColumnKey): boolean {
    return this.tableStore.ctrlColumns.isColumnVisible(columnKey);
  }

  // требования: Связь слоёв с колонками
  // Видимость слоя на Ганте = видимость соответствующего набора колонок таблицы:
  // План скрыт, если скрыта хотя бы одна из колонок плановых дат;
  // Факт — аналогично по факт-колонкам;
  // Прогноз — аналогично по прогноз-колонкам.
  get isPlanVisible(): boolean {
    return (
      this.isColumnVisible("plannedStartDate") &&
      this.isColumnVisible("plannedEndDate")
    );
  }

  get isActualVisible(): boolean {
    return (
      this.isColumnVisible("actualStartDate") &&
      this.isColumnVisible("actualEndDate")
    );
  }

  get isForecastVisible(): boolean {
    return (
      this.isColumnVisible("forecastStartDate") &&
      this.isColumnVisible("forecastEndDate")
    );
  }

  get isWorkVisible(): boolean {
    return (
      this.isColumnVisible("actualStartDate") &&
      this.isColumnVisible("forecastEndDate")
    );
  }

  get tasks(): GanttTask[] {
    const result: GanttTask[] = [];
    const height = Planning2GanttStore.cellHeight;
    this.leftRows.forEach((row, i) => {
      const mkKey = (prefix: keyof GanttTask) => `${prefix}:${row.id}`;

      if (row.kind === "task") {
        const yMin = i * height;
        const yBottomMin = yMin + height / 2;
        const hh = height / 2 - 8;
        const yTopMax = yBottomMin;
        const yTopMin = yTopMax - hh;
        const yBottomMax = yBottomMin + hh;
        const task: GanttTask = {
          key: row.id,
          actual: this.createTaskBar({
            key: mkKey("actual"),
            visible: this.isActualVisible,
            begin: row.actualStartDate,
            end: row.actualEndDate,
            cls: "taskFact",
            y1: yTopMin,
            y2: yTopMax,
          }),
          plan: this.createTaskBar({
            key: mkKey("plan"),
            visible: this.isPlanVisible,
            begin: row.plannedStartDate,
            end: row.plannedEndDate,
            cls: "taskPlan",
            y1: yBottomMin,
            y2: yBottomMax,
          }),
          work: this.createTaskBar({
            key: mkKey("work"),
            visible: this.isWorkVisible && !row.actualEndDate,
            begin: row.actualStartDate,
            end: row.forecastEndDate,
            cls: "taskWork",
            y1: yTopMin,
            y2: yTopMax,
          }),
        };
        if (!task.actual) {
          task.forecast = this.createTaskBar({
            key: mkKey("forecast"),
            visible: this.isForecastVisible,
            begin: row.forecastStartDate,
            end: row.forecastEndDate,
            cls: "taskForecast",
            y1: yTopMin,
            y2: yTopMax,
          });
        }
        // Индикация просрочки показывается только при Отставании > 0 и при отрисованном фактическом баре
        const delay = row.delay ?? 0;
        if (task.actual && delay > 0) {
          const delayWidth = delay * this.dayWidth;
          task.actual.width -= delayWidth;
          task.delay = {
            key: mkKey("delay"),
            y: yTopMin,
            height: hh,
            x: task.actual.x + task.actual.width,
            width: delayWidth,
            cls: "taskDelay",
          };
        }
        result.push(task);
      }
    });
    return result;
  }

  createTaskBar = (par: {
    key: string;
    visible: boolean;
    begin: string | null | undefined;
    end: string | null | undefined;
    cls: string;
    y1: number;
    y2: number;
  }): Bar | undefined => {
    const { key, visible, begin, end, y1, y2, cls } = par;
    if (!visible || !begin || !end) return undefined;
    const x = this.getX(begin);
    const x1 = this.getX(end) + this.dayWidth;
    return {
      key,
      x,
      width: x1 - x - 1,
      y: y1,
      height: y2 - y1,
      cls,
    };
  };

  get arrows(): GanttArrow[] {
    const { leftRows } = this;
    const result: (GanttArrow | undefined)[] = [];
    const dict: Record<string, ZPlanning2Task> = {};
    leftRows.forEach((row) => {
      if (row.kind === "task") {
        dict[row.id] = row;
      }
    });
    leftRows.forEach((dst) => {
      if (dst.kind === "task" && dst.previousTaskId) {
        result.push(this.createArrow(dst.previousTaskId, dst.id));
      }
    });
    return result.filter((it) => !!it);
  }

  get tasksDict(): Record<string, GanttTask> {
    const result: Record<string, GanttTask> = {};
    this.tasks.forEach((row) => {
      result[row.key] = row;
    });
    return result;
  }

  createArrow(srcKey: string, dstKey: string): GanttArrow | undefined {
    const srcTask = this.tasksDict[srcKey];
    const dstTask = this.tasksDict[dstKey];
    if (!srcTask || !dstTask) return undefined;
    // Если есть плановый бар, то используется он. Иначе - совокупность остальных
    const getBars = ({ plan, actual, forecast, delay }: GanttTask): Bar[] => {
      const result = [actual, forecast, delay].filter((bar) => !!bar);
      if (result.length === 0 && plan) {
        return [plan];
      }
      return result;
    };
    const srcBars = getBars(srcTask);
    if (srcBars.length === 0) return undefined;
    const dstBars = getBars(dstTask);
    if (dstBars.length === 0) return undefined;
    const x1 = barsRight(srcBars);
    const x2 = barsLeft(dstBars);
    if (x1 === undefined || x2 === undefined) return undefined;
    const y1 = barMiddle(srcBars[0]!);
    const y2 = barMiddle(dstBars[0]!);
    const h = Planning2GanttStore.cellHeight;
    const ym = Math.floor((y1 < y2 ? y1 + h - 1 : y1) / h) * h;
    const d = 10;
    return {
      key: dstKey,
      points: [
        [x1, y1],
        [x1 + d, y1],
        [x1 + d, ym],
        [x2 - d, ym],
        [x2 - d, y2],
        [x2, y2],
      ],
    };
  }
}

const barRight = (bar: Bar): number => bar.x + bar.width;
const barMiddle = (bar: Bar): number => bar.y + bar.height / 2;

const getLeftTemplateColumns = (columns: Planning2Column[]): string => {
  const widths: string[] = columns.map(({ width }) =>
    typeof width === "number" ? `${width}px` : width,
  );
  // необходима строго фиксированная ширина. иначе нарушится положение зафиксированных слева ячеек
  return widths.map((w) => `minmax(${w}, ${w})`).join(" ");
};

const barsLeft = (bars: (Bar | undefined)[]): number | undefined => {
  let result: number | undefined;
  bars.forEach((bar) => {
    if (bar) {
      const { x } = bar;
      result = result === undefined ? x : Math.min(result, x);
    }
  });
  return result;
};

const barsRight = (bars: (Bar | undefined)[]): number | undefined => {
  let result: number | undefined;
  bars.forEach((bar) => {
    if (bar) {
      const x = barRight(bar);
      result = result === undefined ? x : Math.max(result, x);
    }
  });
  return result;
};

const minDate = (
  a: string | undefined,
  b: string | undefined,
): string | undefined => {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
};

const maxDate = (
  a: string | undefined,
  b: string | undefined,
): string | undefined => {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
};

const minTaskDate = (task: ZPlanning2Task): string | undefined => {
  const { plannedStartDate, actualStartDate, forecastStartDate } = task;
  return [plannedStartDate, actualStartDate, forecastStartDate].reduce(
    (prev: string | undefined, current) => minDate(prev, current ?? undefined),
    undefined,
  );
};

const maxTaskDate = (task: ZPlanning2Task): string | undefined => {
  const { plannedEndDate, actualEndDate, forecastEndDate } = task;
  return [plannedEndDate, actualEndDate, forecastEndDate].reduce(
    (prev: string | undefined, current) => maxDate(prev, current ?? undefined),
    undefined,
  );
};
